import { Controller } from "@hotwired/stimulus";

// Interactive "Wat kost jouw rijbewijs?" calculator.
// Pure client-side: all 2026 CBR tariffs live here as constants, the totals
// recompute live as the visitor adjusts the inputs. No server round-trip,
// no database — the same figures the cost pillar (/blog/kosten-rijbewijs-2026)
// is built on, so the two stay consistent.
export default class extends Controller {
  static targets = [
    "lessenInput", "lessenValue",
    "prijsInput", "prijsValue",
    "theorieInput",
    "cursusInput",
    "tttInput",
    "praktijkInput",
    "faalangstInput",
    "inschrijfInput",
    "outLes", "outTheorie", "outCursus", "outTtt", "outPraktijk",
    "outGezond", "outInschrijf", "outTotal",
    "praktijkLabel", "compare",
    "embedCode", "copyFeedback"
  ];

  // Canonieke host voor de embed-snippet. Bewust hardcoded: de gekopieerde code
  // belandt in de HTML van een andere site en moet daar altijd naar rijdeal.nl
  // wijzen, ongeacht via welk domein (www/rijdeal.nl) de tool bekeken werd.
  static EMBED_ORIGIN = "https://rijdeal.nl";
  static EMBED_PATH = "/rijbewijs-kosten-berekenen/embed";
  static TOOL_PATH = "/rijbewijs-kosten-berekenen";

  // Actuele CBR-tarieven 2026 (in euro)
  static THEORIE = 50.50;
  static PRAKTIJK = 143.50;
  static TTT = 143.50;
  static FAALANGST = 202.00;
  static GEZONDHEIDSVERKLARING = 41.80;
  static CURSUS = { geen: 0, online: 25, klassikaal: 90 };
  static LANDELIJK_GEMIDDELDE = 3320;

  connect() {
    this.recalc();
    if (this.hasEmbedCodeTarget) {
      this.embedCodeTarget.value = this.embedSnippet();
    }
  }

  // De code die rijschoolhouders/bloggers op hun eigen site plakken. De iframe
  // toont de calculator; de attributielink eronder staat in de HTML van de
  // host-pagina en levert de backlink naar rijdeal.nl op.
  embedSnippet() {
    const C = this.constructor;
    const o = C.EMBED_ORIGIN;
    return (
      `<iframe src="${o}${C.EMBED_PATH}" title="Rijbewijskosten berekenen 2026" ` +
      `loading="lazy" style="width:100%;max-width:680px;height:1580px;border:0;` +
      `border-radius:16px;box-shadow:0 1px 4px rgba(0,0,0,.08)"></iframe>\n` +
      `<p style="font:14px/1.5 system-ui,sans-serif;margin:.5rem 0 0">` +
      `<a href="${o}${C.TOOL_PATH}">Rijbewijskosten berekenen</a> — ` +
      `gratis rekentool van Rijdeal</p>`
    );
  }

  recalc() {
    const C = this.constructor;

    const lessen = this.num(this.lessenInputTarget.value);
    const prijs = this.num(this.prijsInputTarget.value);
    const lesTotaal = lessen * prijs;

    const theoriePogingen = Math.max(0, this.num(this.theorieInputTarget.value));
    const theorieTotaal = theoriePogingen * C.THEORIE;

    const cursus = C.CURSUS[this.cursusInputTarget.value] ?? 0;

    const ttt = this.tttInputTarget.checked ? C.TTT : 0;

    const faalangst = this.faalangstInputTarget.checked;
    const praktijkTarief = faalangst ? C.FAALANGST : C.PRAKTIJK;
    const praktijkPogingen = Math.max(0, this.num(this.praktijkInputTarget.value));
    const praktijkTotaal = praktijkPogingen * praktijkTarief;

    const inschrijf = Math.max(0, this.num(this.inschrijfInputTarget.value));
    const gezond = C.GEZONDHEIDSVERKLARING;

    const totaal =
      lesTotaal + theorieTotaal + cursus + ttt + praktijkTotaal + gezond + inschrijf;

    // Live sliderwaarden
    this.lessenValueTarget.textContent = `${lessen} ${lessen === 1 ? "les" : "lessen"}`;
    this.prijsValueTarget.textContent = this.eur(prijs);

    // Uitsplitsing
    this.outLesTarget.textContent = this.eur(lesTotaal);
    this.outTheorieTarget.textContent = this.eur(theorieTotaal);
    this.outCursusTarget.textContent = this.eur(cursus);
    this.outTttTarget.textContent = this.eur(ttt);
    this.outPraktijkTarget.textContent = this.eur(praktijkTotaal);
    this.outGezondTarget.textContent = this.eur(gezond);
    this.outInschrijfTarget.textContent = this.eur(inschrijf);
    this.outTotalTarget.textContent = this.eur(totaal);

    this.praktijkLabelTarget.textContent = faalangst
      ? "Faalangstexamen (i.p.v. regulier)"
      : "Praktijkexamen";

    // Vergelijking met landelijk gemiddelde
    const diff = Math.round(totaal - C.LANDELIJK_GEMIDDELDE);
    const gem = this.eur(C.LANDELIJK_GEMIDDELDE, 0);
    if (Math.abs(diff) <= 25) {
      this.compareTarget.textContent =
        `Dat is vrijwel gelijk aan het landelijk gemiddelde van ${gem}.`;
    } else if (diff < 0) {
      this.compareTarget.textContent =
        `Dat is ${this.eur(Math.abs(diff), 0)} onder het landelijk gemiddelde van ${gem}.`;
    } else {
      this.compareTarget.textContent =
        `Dat is ${this.eur(diff, 0)} boven het landelijk gemiddelde van ${gem}.`;
    }
  }

  step(event) {
    const button = event.currentTarget;
    const targetName = button.dataset.field;
    const delta = parseInt(button.dataset.delta, 10);
    const input = this[`${targetName}InputTarget`];
    if (!input) return;
    const next = Math.max(0, this.num(input.value) + delta);
    input.value = next;
    this.recalc();
  }

  async copyLink(event) {
    const C = this.constructor;
    const url = `${C.EMBED_ORIGIN}${C.TOOL_PATH}`;
    await this.copy(url, event.currentTarget);
  }

  async copyEmbed(event) {
    await this.copy(this.embedSnippet(), event.currentTarget);
  }

  async copy(text, button) {
    try {
      await navigator.clipboard.writeText(text);
      const original = button.textContent;
      button.textContent = "Gekopieerd!";
      button.classList.add("bg-emerald-600", "text-white");
      setTimeout(() => {
        button.textContent = original;
        button.classList.remove("bg-emerald-600", "text-white");
      }, 1800);
    } catch (_) {
      // Clipboard kan geweigerd worden (bv. zonder https of permissies).
      if (this.hasCopyFeedbackTarget) {
        this.copyFeedbackTarget.textContent = text;
        this.copyFeedbackTarget.classList.remove("hidden");
      }
    }
  }

  num(value) {
    const n = parseFloat(String(value).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }

  eur(value, fractionDigits = 2) {
    return value.toLocaleString("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    });
  }
}
